// src/data/username-claim.ts
import { db } from "@/lib/firebase";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";

/** Routes/keywords we should not allow as usernames (normalized to lowercase). */
const RESERVED = new Set<string>([
  "", "app", "admin", "api", "about", "help", "support", "contact",
  "login", "logout", "signup", "register", "reset-password", "forgot",
  "settings", "dashboard", "home", "inbox", "search", "marketplace",
  "collection", "followers", "splash", "landing",
  // onboarding/flows
  "termsack", "claim-username",
  // legal
  "legal", "terms", "privacy", "community", "copyright", "data", "dmca",
  // common singletons
  "me", "user", "users", "profile", "profiles",
]);

const USERNAME_RE = /^[a-z0-9._-]{3,32}$/;

/**
 * Claims a username for the given uid in a single transaction.
 * - All reads happen before any writes (required by Firestore).
 * - If the username exists and belongs to someone else, throws.
 * - If it is free or already belongs to this uid, (up)sets both docs.
 * - If the user previously had a different username, removes the old mapping.
 */
export async function claimUsername(uid: string, rawName: string) {
  const input = (rawName ?? "").trim();
  const username = input.toLowerCase();

  if (!uid) throw new Error("Not signed in.");
  if (!username) throw new Error("Choose a username.");
  if (!USERNAME_RE.test(username)) {
    throw new Error("Usernames must be 3–32 chars: letters, numbers, . _ -");
  }
  if (RESERVED.has(username)) {
    throw new Error("That name is reserved. Please choose another.");
  }

  await runTransaction(db, async (tx) => {
    const unameRef = doc(db, "usernames", username);
    const userRef = doc(db, "users", uid);

    // ----- READS (all reads must happen before any writes) -----
    const unameSnap = await tx.get(unameRef);
    const userSnap = await tx.get(userRef);

    // Check if desired username is already taken by someone else
    if (unameSnap.exists()) {
      const existingUid = (unameSnap.data() as { uid?: string })?.uid;
      if (existingUid && existingUid !== uid) {
        throw new Error("Username is already taken.");
      }
      // else it already belongs to this uid — we will rewrite/refresh timestamps below
    }

    // Determine previous username (if any) to clean up the old mapping
    const prevUsername: string | null =
      (userSnap.exists() &&
        typeof (userSnap.data() as any)?.username === "string" &&
        ((userSnap.data() as any).username as string).toLowerCase()) ||
      null;

    let prevRefSnap: FirebaseFirestoreTypes.DocumentSnapshot | null = null;
    let prevRefPath = "";
    if (prevUsername && prevUsername !== username) {
      const prevRef = doc(db, "usernames", prevUsername);
      prevRefPath = prevRef.path;
      prevRefSnap = await tx.get(prevRef); // still a READ phase
    }

    // ----- WRITES (only after all reads above) -----
    const now = serverTimestamp();

    // Upsert the mapping for the desired username -> uid
    tx.set(
      unameRef,
      { uid, createdAt: now, updatedAt: now },
      { merge: true },
    );

    // Update the user document with canonical + display/cased mirrors
    tx.set(
      userRef,
      {
        username,               // canonical (lowercase)
        username_cased: input,  // display mirror (keeps user’s chosen casing)
        username_updatedAt: now,
        updatedAt: now,
      },
      { merge: true },
    );

    // If user had a different username previously, and that mapping still points to them, delete it
    if (prevUsername && prevUsername !== username && prevRefSnap?.exists()) {
      const prevOwner = (prevRefSnap.data() as { uid?: string })?.uid;
      if (prevOwner === uid) {
        tx.delete(doc(db, "usernames", prevUsername));
      }
    }
  });

  return username;
}

